import React from 'react';
import LegalLayout from '../components/layout/LegalLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiUser, 
  FiShield, 
  FiSmartphone, 
  FiCloud, 
  FiSettings, 
  FiCheckCircle, 
  FiLock, 
  FiEyeOff, 
  FiDatabase,
  FiMail,
  FiFileText,
  FiClock,
  FiShare2
} = FiIcons;

const navLinks = [
  { label: 'Privacy at a Glance', href: '#summary' },
  { label: '1. Who we are', href: '#who-we-are' },
  { label: '2. Data we collect', href: '#data-collected' },
  { label: '3. Legal basis', href: '#legal-basis' },
  { label: '4. Storage & Security', href: '#storage' },
  { label: '5. Data sharing', href: '#sharing' },
  { label: '6. Data retention', href: '#retention' },
  { label: '7. Your rights', href: '#rights' },
  { label: '8. Contact us', href: '#contact' },
];

const SectionIcon = ({ icon }) => (
  <div className="w-12 h-12 rounded-2xl bg-paper border border-line flex items-center justify-center text-rose mb-6 shadow-sm">
    <SafeIcon icon={icon} className="w-6 h-6" />
  </div>
);

const Privacy = () => {
  return (
    <LegalLayout 
      title="Privacy Policy" 
      lastUpdated="19 June 2026" 
      navLinks={navLinks}
    >
      <div className="mb-16">
        <p className="text-xl text-muted leading-relaxed font-light">
          At Nisaa, we believe that your health and spiritual journey are deeply personal. This Privacy Policy explains how <strong>Sussex Endocrine Clinic</strong> collects, uses, and protects your information when you use the Nisaa mobile application and website.
        </p>
      </div>

      {/* Privacy at a Glance */}
      <section id="summary" className="scroll-mt-32">
        <SectionIcon icon={FiShield} />
        <h2>Privacy at a Glance</h2>
        <p className="mb-8">We’ve designed Nisaa to be as private as a physical journal. Here is the summary of our commitments to you:</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-8 not-prose">
          {[
            { 
              title: "No Ads", 
              desc: "We never show ads or use tracking SDKs from ad networks.",
              icon: FiEyeOff 
            },
            { 
              title: "Local First", 
              desc: "By default, your tracking data never leaves your smartphone.",
              icon: FiSmartphone 
            },
            { 
              title: "No Data Selling", 
              desc: "We never have, and never will, sell your personal information.",
              icon: FiLock 
            },
            { 
              title: "Full Control", 
              desc: "Delete your data and account instantly from app settings.",
              icon: FiDatabase 
            }
          ].map((item, i) => (
            <div key={i} className="p-6 rounded-[24px] bg-white border border-line/60 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3 text-rose">
                <SafeIcon icon={item.icon} className="w-5 h-5" />
                <span className="font-bold text-aubergine">{item.title}</span>
              </div>
              <p className="text-sm text-muted leading-relaxed m-0">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 1. Who We Are */}
      <section id="who-we-are" className="scroll-mt-32">
        <SectionIcon icon={FiUser} />
        <h2>1. Who we are</h2>
        <p>
          Nisaa is operated by <strong>Sussex Endocrine Clinic</strong>, a medical practice based in the United Kingdom. Under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, we act as the <strong>Data Controller</strong> for the personal data collected through the Nisaa app.
        </p>
      </section>

      {/* 2. Data We Collect */}
      <section id="data-collected" className="scroll-mt-32">
        <SectionIcon icon={FiFileText} />
        <h2>2. Data we collect</h2>
        <p>We only collect the information necessary to provide you with Nisaa's features. This includes:</p>
        
        <h3>A. Account Information</h3>
        <p>If you choose to create an account for cloud backups, we collect your email address and a securely hashed password.</p>

        <h3>B. Health & Spiritual Data (Special Category Data)</h3>
        <p>When you use the app, you may choose to input sensitive information, which is stored locally on your device by default:</p>
        <ul>
          <li><strong>Cycle Logs:</strong> Period dates, symptoms, moods, and cervical mucus observations.</li>
          <li><strong>Pregnancy Data:</strong> Due dates, baby’s movements, and personal notes.</li>
          <li><strong>Spiritual Records:</strong> Prayer settings, fasting logs, and ghusl tracking.</li>
        </ul>

        <h3>C. Technical & Device Data</h3>
        <p>We may collect minimal technical data such as your device type, operating system version, and general location (to calculate Qibla and prayer times). <em>Note: Location calculations happen entirely locally on your device and are not transmitted to our servers.</em></p>
      </section>

      {/* 3. Legal Basis */}
      <section id="legal-basis" className="scroll-mt-32">
        <SectionIcon icon={FiSettings} />
        <h2>3. Legal basis for processing</h2>
        <p>Under UK GDPR, we require a valid legal basis to process your data:</p>
        <ul>
          <li><strong>Explicit Consent (Article 9 UK GDPR):</strong> Because health and religious data are considered "Special Category Data," we rely on your explicit consent to process and (if opted-in) back up this data. You can withdraw this consent at any time by deleting your account.</li>
          <li><strong>Contractual Necessity:</strong> To provide you with account creation and cloud backup services.</li>
          <li><strong>Legitimate Interests:</strong> To improve our app's performance, fix bugs, and ensure security.</li>
        </ul>
      </section>

      {/* 4. Storage & Security */}
      <section id="storage" className="scroll-mt-32">
        <SectionIcon icon={FiCloud} />
        <h2>4. Storage & Security</h2>
        
        <h3>Local-First Architecture</h3>
        <p>By default, Nisaa operates on a "Local-First" basis. Your health and spiritual data are stored exclusively in your device's internal storage. If you do not create an account, we have no access to your data.</p>

        <h3>Encrypted Cloud Backups</h3>
        <p>If you opt-in to create an account, your data is securely synced to our cloud provider, <strong>Supabase</strong>. All data transmitted between your device and Supabase is encrypted in transit (using TLS) and encrypted at rest on secure servers.</p>
      </section>

      {/* 5. Data Sharing */}
      <section id="sharing" className="scroll-mt-32">
        <SectionIcon icon={FiShare2} />
        <h2>5. Data sharing and third parties</h2>
        <p><strong>We do not sell, rent, or trade your personal data to anyone.</strong></p>
        <p>We only share your data with trusted infrastructure providers (Data Processors) strictly necessary to run the app:</p>
        <ul>
          <li><strong>Supabase:</strong> Provides secure database hosting and user authentication.</li>
          <li><strong>Apple/Google:</strong> Provide secure app distribution and optional crash analytics (only if you opt-in via your device settings).</li>
        </ul>
        <p>We will only disclose your data to law enforcement if we are legally compelled to do so by a valid UK court order.</p>
      </section>

      {/* 6. Data Retention */}
      <section id="retention" className="scroll-mt-32">
        <SectionIcon icon={FiClock} />
        <h2>6. Data retention</h2>
        <p>We retain your personal data only for as long as your account is active. If you choose to delete your account via the app settings:</p>
        <ul>
          <li>All your health and spiritual data is immediately and permanently deleted from our cloud servers.</li>
          <li>Your account credentials are permanently erased.</li>
          <li>Data stored locally on your device will be retained until you uninstall the app or clear the app's local data.</li>
        </ul>
      </section>

      {/* 7. Your Rights */}
      <section id="rights" className="scroll-mt-32">
        <SectionIcon icon={FiShield} />
        <h2>7. Your Privacy Rights</h2>
        <p>
          As a user protected by the UK GDPR and DPA 2018, you maintain profound control over your data. You have the right to:
        </p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the data we hold about you (easily exportable directly within the app).</li>
          <li><strong>Rectification:</strong> Correct any inaccurate data.</li>
          <li><strong>Erasure (Right to be Forgotten):</strong> Delete your account and all associated cloud data instantly via the app settings.</li>
          <li><strong>Restriction & Objection:</strong> Opt-out of specific data processing activities.</li>
        </ul>
        <p>To exercise any of these rights outside of the app's built-in tools, please contact us at the email below.</p>
      </section>

      {/* 8. Contact */}
      <section id="contact" className="scroll-mt-32">
        <SectionIcon icon={FiMail} />
        <h2>8. Contact Us</h2>
        <p>If you have any questions, concerns, or wish to exercise your data rights, please contact our Data Protection Officer at:</p>
        
        <div className="mt-8 p-8 rounded-[32px] bg-aubergine text-white not-prose relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-rose/10 rounded-full blur-3xl -mr-16 -mt-16" />
          <div className="relative z-10">
            <p className="font-bold text-xl mb-1">Sussex Endocrine Clinic</p>
            <p className="text-white/60 text-sm mb-6">United Kingdom</p>
            <a 
              href="mailto:contact@sussexendocrineclinic.co.uk" 
              className="inline-flex items-center gap-2 text-rose hover:text-white transition-colors font-bold text-lg"
            >
              contact@sussexendocrineclinic.co.uk
              <SafeIcon icon={FiIcons.FiArrowRight} className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </LegalLayout>
  );
};

export default Privacy;